/* global BX24 */
import React, { useCallback, useEffect, useMemo, useState } from "react";
import { createPortal } from "react-dom";
import { ChevronLeft, ChevronRight, X } from "react-bootstrap-icons";
import { getImageSrc } from "../../utils/imageSrc";

/**
 * Подтянуть окно Битрикса к началу приложения.
 *
 * В Битриксе калькулятор живёт в iframe, высота которого равна высоте его
 * содержимого: прокручивается не он, а страница сделки вокруг него. Поэтому
 * `position: fixed` внутри iframe прилипает не к экрану, а к НАЧАЛУ приложения,
 * и окно с фотографией открывается где-то вверху — менеджеру приходится листать
 * страницу вверх, чтобы его увидеть (13.10.2026, Роман).
 *
 * Переставить само окно нельзя: изнутри iframe не видно, какая его часть сейчас
 * на экране. Зато можно прокрутить родительское окно к началу приложения — там
 * окно и находится. Вне Битрикса BX24 нет, и прокрутка не нужна: fixed работает
 * как обычно.
 */
const podtyanutOknoBitriksa = () => {
  if (typeof BX24 === "undefined" || BX24 === null) {
    return;
  }

  try {
    BX24.scrollParentWindow(0);
  } catch (error) {
    // Прокрутка — удобство, а не условие показа: окно откроется в любом случае.
    console.log("Не удалось прокрутить окно Битрикса к фотографии:", error.message);
  }
};

const NomenclatureImagesModal = ({ isOpen, title, imageUrls, isLoading, onClose }) => {
  const images = useMemo(
    () => (Array.isArray(imageUrls) ? imageUrls.filter(Boolean) : []),
    [imageUrls]
  );
  const [currentIndex, setCurrentIndex] = useState(0);
  const hasManyImages = images.length > 1;

  const showPreviousImage = useCallback(() => {
    setCurrentIndex((prev) => (prev === 0 ? images.length - 1 : prev - 1));
  }, [images.length]);

  const showNextImage = useCallback(() => {
    setCurrentIndex((prev) => (prev + 1) % images.length);
  }, [images.length]);

  useEffect(() => {
    if (isOpen) {
      setCurrentIndex(0);
      podtyanutOknoBitriksa();
    }
  }, [isOpen, imageUrls]);

  useEffect(() => {
    if (!isOpen) {
      return undefined;
    }

    const handleKeyDown = (event) => {
      if (event.key === "Escape") {
        onClose();
      }

      if (!hasManyImages) {
        return;
      }

      if (event.key === "ArrowLeft") {
        showPreviousImage();
      }

      if (event.key === "ArrowRight") {
        showNextImage();
      }
    };

    document.addEventListener("keydown", handleKeyDown);

    return () => {
      document.removeEventListener("keydown", handleKeyDown);
    };
  }, [hasManyImages, isOpen, onClose, showNextImage, showPreviousImage]);

  if (!isOpen) {
    return null;
  }

  const currentImageUrl = images[currentIndex];

  // - Внутри Битрикса окно прижимается к началу приложения, а не к середине
  //   (13.10.2026, Роман). Высота iframe равна высоте содержимого, поэтому
  //   «по центру экрана» означало бы середину длинной страницы — туда ещё надо
  //   долистать. К началу же приложения окно Битрикса подтягивается при открытии.
  const vBitrikse = typeof BX24 !== "undefined" && BX24 !== null;

  const modalContent = (
    <div
      onClick={onClose}
      style={{
        position: "fixed",
        inset: 0,
        backgroundColor: "rgba(0, 0, 0, 0.55)",
        display: "flex",
        alignItems: vBitrikse ? "flex-start" : "center",
        justifyContent: "center",
        padding: "20px",
        zIndex: 2147483647,
      }}
    >
      <div
        onClick={(event) => event.stopPropagation()}
        style={{
          position: "relative",
          background: "#fff",
          borderRadius: "12px",
          padding: "20px",
          width: "min(960px, 90vw)",
          // В Битриксе высоту считаем не от вьюпорта: 90vh там — это 90% всей
          // страницы приложения, и окно вытянулось бы на несколько экранов.
          maxHeight: vBitrikse ? "640px" : "90vh",
          boxShadow: "0 12px 40px rgba(0, 0, 0, 0.25)",
          overflow: "auto",
        }}
      >
        <button
          type="button"
          onClick={onClose}
          aria-label="Закрыть"
          style={{
            position: "absolute",
            top: "10px",
            right: "10px",
            border: "none",
            background: "transparent",
            fontSize: "24px",
            cursor: "pointer",
            lineHeight: 1,
          }}
        >
          <X size={24} />
        </button>

        <div style={{ margin: "0 36px 14px", fontSize: "20px", fontWeight: 600, textAlign: "center" }}>
          {title || "Изображение"}
        </div>

        {isLoading ? (
          <div style={{ minHeight: "260px", display: "flex", alignItems: "center", justifyContent: "center" }}>
            Загрузка...
          </div>
        ) : currentImageUrl ? (
          <>
            <div style={{ position: "relative", minHeight: "260px", display: "flex", alignItems: "center", justifyContent: "center" }}>
              {hasManyImages && (
                <button
                  type="button"
                  onClick={showPreviousImage}
                  aria-label="Предыдущее изображение"
                  style={{
                    position: "absolute",
                    left: 0,
                    top: "50%",
                    transform: "translateY(-50%)",
                    width: "40px",
                    height: "40px",
                    border: "none",
                    borderRadius: "50%",
                    background: "rgba(255, 255, 255, 0.9)",
                    boxShadow: "0 2px 10px rgba(0, 0, 0, 0.16)",
                    color: "#007bff",
                    cursor: "pointer",
                    display: "flex",
                    alignItems: "center",
                    justifyContent: "center",
                  }}
                >
                  <ChevronLeft size={22} />
                </button>
              )}

              <img
                src={getImageSrc(currentImageUrl)}
                alt={title || "Изображение номенклатуры"}
                style={{
                  display: "block",
                  maxWidth: "100%",
                  // В Битриксе vh — высота всей страницы приложения, а не экрана,
                  // поэтому там высота задаётся пикселями под размер окна.
                  maxHeight: vBitrikse ? "520px" : "72vh",
                  objectFit: "contain",
                  margin: "0 auto",
                }}
              />

              {hasManyImages && (
                <button
                  type="button"
                  onClick={showNextImage}
                  aria-label="Следующее изображение"
                  style={{
                    position: "absolute",
                    right: 0,
                    top: "50%",
                    transform: "translateY(-50%)",
                    width: "40px",
                    height: "40px",
                    border: "none",
                    borderRadius: "50%",
                    background: "rgba(255, 255, 255, 0.9)",
                    boxShadow: "0 2px 10px rgba(0, 0, 0, 0.16)",
                    color: "#007bff",
                    cursor: "pointer",
                    display: "flex",
                    alignItems: "center",
                    justifyContent: "center",
                  }}
                >
                  <ChevronRight size={22} />
                </button>
              )}
            </div>

            {hasManyImages && (
              <div style={{ marginTop: "12px", textAlign: "center", color: "#6c757d", fontSize: "14px" }}>
                {images.map((item, index) => (
                  <button
                    key={`${item.image_url}-${index}`}
                    type="button"
                    onClick={() => setCurrentIndex(index)}
                    aria-label="Показать картинку"
                    style={{
                      width: "74px",
                      height: "74px",
                      padding: "4px",
                      border: "1px solid #d8dde3",
                      borderRadius: "8px",
                      cursor: "pointer",
                      flexShrink: 0,
                      backgroundColor: "#fff",
                      boxSizing: "border-box",
                      transition: "border-color 120ms ease, box-shadow 120ms ease",
                      ...(index === currentIndex) && {
                        border: "2px solid #1976d2",
                        boxShadow: "0 0 0 3px rgba(25, 118, 210, 0.14)"
                      }
                    }}
                  >
                    <img
                      src={getImageSrc(item)}
                      alt=''
                      style={{
                        width: "100%",
                        height: "100%",
                        objectFit: "contain",
                        borderRadius: "6px",
                        display: "block",
                        backgroundColor: "#f8f9fa",
                      }}
                    />
                  </button>
                ))}
              </div>
            )}
          </>
        ) : (
          <div style={{ minHeight: "160px", display: "flex", alignItems: "center", justifyContent: "center", textAlign: "center" }}>
            Изображения для этой номенклатуры не найдены
          </div>
        )}
      </div>
    </div>
  );

  return createPortal(modalContent, document.body);
};

export default NomenclatureImagesModal;
